import { collectionGroup, query, where } from 'firebase/firestore'
import { getDocs } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { plainPreview } from '@/lib/markdown'
import type { CommentParentKind } from '@/lib/comments'
import type { ExportColumn } from '@/lib/export'

/**
 * The discussion, in the file.
 *
 * A register's export used to carry only the row's own fields, so the Notes column came
 * out and the conversation beside it did not — and on a decision the conversation is
 * usually where the reasoning is. Somebody reading the CSV on a plane got the answer
 * without the argument.
 *
 * Fetched when the export runs, never before. It is one collection-group read per export
 * covering every comment of that kind, which is a real cost on a free-tier project: doing
 * it on page load would spend it on every visit, whether or not anybody exported anything.
 */

export type ExportComment = {
  id: string
  /** The document id of the item the comment hangs under. */
  itemId: string
  /** The comment this one replies to, when it is a reply. */
  replyTo: string | null
  body: string
  byName: string | null
  byEmail: string
  at: { seconds: number } | null
}

/** `2026-09-05` — unambiguous wherever the file is opened. */
const day = (at: { seconds: number } | null): string =>
  at ? new Date(at.seconds * 1000).toISOString().slice(0, 10) : ''

const who = (c: ExportComment): string => c.byName ?? c.byEmail ?? 'Unknown'

/** `Andy Haas · 2026-09-05`, or just the name when the comment has not been stamped yet. */
export const byline = (c: ExportComment): string => {
  const stamp = day(c.at)
  return stamp ? `${who(c)} · ${stamp}` : who(c)
}

const oldestFirst = (a: ExportComment, b: ExportComment) =>
  (a.at?.seconds ?? 0) - (b.at?.seconds ?? 0)

/**
 * One comment as a line. Markdown markers come out; the words stay.
 *
 * The date is dropped rather than left blank when there is none — `at` is a server
 * timestamp, and it is null for the moment between somebody pressing Post and the server
 * answering, which would otherwise print as a stray separator.
 */
const line = (c: ExportComment, reply: boolean): string =>
  `${reply ? '↳ ' : ''}${byline(c)}: ${plainPreview(c.body)}`.trimEnd()

export type CommentThread = ExportComment & { replies: ExportComment[] }

/**
 * Every item's discussion, as threads.
 *
 * Oldest first, with each reply under the comment it answers, because a thread read out
 * of order is worse than no thread. A reply whose parent is missing — deleted, or written
 * before threading existed — is promoted to a top-level entry rather than dropped: a
 * comment nobody can see is the one failure this is meant to prevent.
 *
 * The structure is kept rather than flattened here because the two exports want different
 * things from it. A CSV cell can hold a block of text and nothing else; markdown can hold
 * a nested list, and should.
 */
export function threadsByItem(comments: ExportComment[]): Map<string, CommentThread[]> {
  const byItem = new Map<string, ExportComment[]>()
  for (const c of comments) {
    const list = byItem.get(c.itemId)
    if (list) list.push(c)
    else byItem.set(c.itemId, [c])
  }

  const out = new Map<string, CommentThread[]>()
  for (const [itemId, all] of byItem) {
    const ids = new Set(all.map((c) => c.id))
    const replies = new Map<string, ExportComment[]>()
    const roots: ExportComment[] = []
    for (const c of all) {
      if (c.replyTo && ids.has(c.replyTo)) {
        const list = replies.get(c.replyTo)
        if (list) list.push(c)
        else replies.set(c.replyTo, [c])
      } else {
        roots.push(c)
      }
    }
    out.set(itemId, roots.sort(oldestFirst).map((root) => ({
      ...root,
      replies: (replies.get(root.id) ?? []).sort(oldestFirst),
    })))
  }
  return out
}

/** Every item's discussion, flattened to one block of text each — what a CSV cell takes. */
export function discussionByItem(comments: ExportComment[]): Map<string, string> {
  const out = new Map<string, string>()
  for (const [itemId, threads] of threadsByItem(comments)) {
    const lines: string[] = []
    for (const thread of threads) {
      lines.push(line(thread, false))
      for (const reply of thread.replies) lines.push(line(reply, true))
    }
    out.set(itemId, lines.join('\n'))
  }
  return out
}

/**
 * Every comment of one kind.
 *
 * One query per kind, the same shape the global search uses and for the same reason:
 * `firestore.rules` gates the collection group on the `parentKind` each comment carries,
 * and Firestore refuses a whole query when any document in it would be denied. Asking for
 * one label at a time is what makes the read provably allowed.
 */
export async function fetchComments(kind: CommentParentKind): Promise<ExportComment[]> {
  const snap = await getDocs(query(collectionGroup(db, 'comments'), where('parentKind', '==', kind)))
  const rows: ExportComment[] = []
  for (const d of snap.docs) {
    // `comments` is always a subcollection of the item, so the grandparent is the item.
    const itemId = d.ref.parent.parent?.id
    if (!itemId) continue
    const data = d.data() as Record<string, unknown>
    rows.push({
      id: d.id,
      itemId,
      replyTo: (data.parentId as string | null) ?? null,
      body: (data.body as string) ?? '',
      byName: (data.byName as string | null) ?? null,
      byEmail: (data.byEmail as string) ?? '',
      at: (data.at as { seconds: number } | null) ?? null,
    })
  }
  return rows
}

/** The Discussion column a CSV takes: the whole thread as one block of text. */
export function discussionColumn<T extends { id: string }>(
  threads: Map<string, string>,
): ExportColumn<T> {
  return { header: 'Discussion', value: (row: T) => threads.get(row.id) ?? '' }
}

/**
 * One comment as JSON. Structure rather than a rendered line, because the reason this
 * file exists is for something else to read it — the body is left as it was written,
 * markdown and all, since nothing here has to fit in a cell.
 */
export type JsonComment = {
  by: string
  at: string | null
  body: string
  replies?: JsonComment[]
}

const jsonComment = (c: ExportComment): JsonComment => ({
  by: who(c),
  at: c.at ? new Date(c.at.seconds * 1000).toISOString() : null,
  body: c.body,
})

/** An item's discussion as nested JSON — the shape a CSV cell cannot hold. */
export function jsonDiscussion(threads: CommentThread[] | undefined): JsonComment[] {
  return (threads ?? []).map((thread) => {
    const entry = jsonComment(thread)
    if (thread.replies.length > 0) entry.replies = thread.replies.map(jsonComment)
    return entry
  })
}
